import React, { useState, useCallback, useEffect } from "react";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import { AVAILABLE_STATUSES } from "../../../utils/constants";
import "./MerchantFilters.scss";

/**
 * MerchantFilters Component
 * Props:
 * - modernFilters: current filter state
 * - onFilterChange: callback for filter changes
 * - onStatusChange: callback for status filter changes
 * - company: company name for business name label context
 * - filterLoadingStates: object containing loading states for each filter
 * - clearHandlers: object containing clear handlers for each filter
 */
const MerchantFilters = ({
  modernFilters,
  onFilterChange,
  onStatusChange,
  company,
  filterLoadingStates = {},
  clearHandlers = {},
}) => {
  // These are the actual statuses used in Zoho Leads
  const availableStatuses = AVAILABLE_STATUSES;

  // UI states for dropdowns and date pickers
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isSubmittedDateOpen, setIsSubmittedDateOpen] = useState(false);
  const [isApprovalDateOpen, setIsApprovalDateOpen] = useState(false);
  const [isLastUpdatedDateOpen, setIsLastUpdatedDateOpen] = useState(false);

  // State for search and custom status input
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [customStatusInput, setCustomStatusInput] = useState("");

  // Get floating label text based on company
  const getBusinessNameLabel = () => {
    if (company === "CyberSource") {
      return "Search Business Name, Account EX, or Email";
    } else {
      // For all other companies (Authorize.net, FreedomMerchant, Vb360, etc.)
      return "Search Business Name, Sales Rep, or Email";
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isStatusDropdownOpen && !event.target.closest(".status-dropdown")) {
        setIsStatusDropdownOpen(false);
      }
      if (
        isSubmittedDateOpen &&
        !event.target.closest(".submitted-date-picker")
      ) {
        setIsSubmittedDateOpen(false);
      }
      if (
        isApprovalDateOpen &&
        !event.target.closest(".approval-date-picker")
      ) {
        setIsApprovalDateOpen(false);
      }
      if (
        isLastUpdatedDateOpen &&
        !event.target.closest(".last-updated-date-picker")
      ) {
        setIsLastUpdatedDateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isStatusDropdownOpen,
    isSubmittedDateOpen,
    isApprovalDateOpen,
    isLastUpdatedDateOpen,
  ]);

  // Event handlers
  const toggleStatusDropdown = useCallback(() => {
    setIsStatusDropdownOpen((prev) => !prev);
  }, []);

  const toggleSubmittedDatePicker = useCallback(() => {
    setIsSubmittedDateOpen((prev) => !prev);
    setIsApprovalDateOpen(false);
    setIsLastUpdatedDateOpen(false);
  }, []);

  const toggleApprovalDatePicker = useCallback(() => {
    setIsApprovalDateOpen((prev) => !prev);
    setIsSubmittedDateOpen(false);
    setIsLastUpdatedDateOpen(false);
  }, []);

  const toggleLastUpdatedDatePicker = useCallback(() => {
    setIsLastUpdatedDateOpen((prev) => !prev);
    setIsSubmittedDateOpen(false);
    setIsApprovalDateOpen(false);
  }, []);

  // Handle preset selection for date ranges
  const handlePresetSelect = useCallback(
    (preset, dateRangeType) => {
      // Pass preset selection to parent with range type info
      onFilterChange(dateRangeType, { preset, rangeType: dateRangeType });
    },
    [onFilterChange]
  );

  // Handle removing individual status
  const handleRemoveStatus = useCallback(
    (statusToRemove) => {
      onStatusChange(statusToRemove, false);
    },
    [onStatusChange]
  );

  // Handle adding custom status
  const handleAddCustomStatus = useCallback(() => {
    const trimmedStatus = customStatusInput.trim();
    if (trimmedStatus && !modernFilters.status.includes(trimmedStatus)) {
      onStatusChange(trimmedStatus, true);
      setCustomStatusInput("");
    }
  }, [customStatusInput, modernFilters.status, onStatusChange]);

  // Handle Enter key press in custom status input
  const handleCustomStatusKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddCustomStatus();
      }
    },
    [handleAddCustomStatus]
  );

  // Filter statuses based on search term
  const filteredStatuses = useCallback(() => {
    if (!statusSearchTerm.trim()) {
      return availableStatuses;
    }
    return availableStatuses.filter((status) =>
      status.toLowerCase().includes(statusSearchTerm.toLowerCase())
    );
  }, [statusSearchTerm, availableStatuses]);

  // Handle keyboard navigation in dropdown
  const handleDropdownKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setIsStatusDropdownOpen(false);
    }
  }, []);

  // Clear status search when dropdown closes
  useEffect(() => {
    if (!isStatusDropdownOpen) {
      setStatusSearchTerm("");
    }
  }, [isStatusDropdownOpen]);

  return (
    <section className="filters-section">
      <div className="filters-container">
        {/* Single Row - All Filters */}
        <div className="filter-row">
          <div className="table-search-fileter-group">
            <div className="floating-input-group search-business-name">
              <input
                type="text"
                placeholder=" "
                value={modernFilters.businessName}
                onChange={(e) => onFilterChange("businessName", e.target.value)}
                className={`floating-input ${
                  modernFilters.businessName ? "has-value" : ""
                }`}
              />
              <label className="floating-label">{getBusinessNameLabel()}</label>
              {filterLoadingStates.businessName && (
                <div className="search-loading">
                  <div className="simple-spinner"></div>
                </div>
              )}
              {modernFilters.businessName && !filterLoadingStates.businessName && (
                <button
                  type="button"
                  className="clear-input-btn"
                  onClick={clearHandlers.businessName}
                  title="Clear Search"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            <div className="floating-input-group gateway-id-input">
              <input
                type="text"
                placeholder=" "
                value={modernFilters.gatewayId}
                onChange={(e) => onFilterChange("gatewayId", e.target.value)}
                className={`floating-input ${
                  modernFilters.gatewayId ? "has-value" : ""
                }`}
              />
              <label className="floating-label">Gateway ID</label>
              {filterLoadingStates.gatewayId && (
                <div className="search-loading">
                  <div className="simple-spinner"></div>
                </div>
              )}
              {modernFilters.gatewayId && !filterLoadingStates.gatewayId && (
                <button
                  type="button"
                  className="clear-input-btn"
                  onClick={clearHandlers.gatewayId}
                  title="Clear Gateway ID"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            <div className="submitted-date-picker">
              <DateRangePicker
                isOpen={isSubmittedDateOpen}
                dateRange={modernFilters.submittedDateRange}
                onDateChange={(dateRange) =>
                  onFilterChange("submittedDateRange", dateRange)
                }
                onPresetSelect={(preset) =>
                  handlePresetSelect(preset, "submittedDateRange")
                }
                onClear={clearHandlers.submittedDate}
                onToggle={toggleSubmittedDatePicker}
                label="Submitted Date Range"
                isLoading={filterLoadingStates.submittedDate}
                className="submitted-date-picker"
              />
            </div>
            <div className="approval-date-picker">
              <DateRangePicker
                isOpen={isApprovalDateOpen}
                dateRange={modernFilters.approvalDateRange}
                onDateChange={(dateRange) =>
                  onFilterChange("approvalDateRange", dateRange)
                }
                onPresetSelect={(preset) =>
                  handlePresetSelect(preset, "approvalDateRange")
                }
                onClear={clearHandlers.approvalDate}
                onToggle={toggleApprovalDatePicker}
                label="Approval Date Range"
                isLoading={filterLoadingStates.approvalDate}
                className="approval-date-picker"
              />
            </div>
            <div className="last-updated-date-picker">
              <DateRangePicker
                isOpen={isLastUpdatedDateOpen}
                dateRange={modernFilters.lastUpdatedDateRange}
                onDateChange={(dateRange) =>
                  onFilterChange("lastUpdatedDateRange", dateRange)
                }
                onPresetSelect={(preset) =>
                  handlePresetSelect(preset, "lastUpdatedDateRange")
                }
                onClear={clearHandlers.lastUpdatedDate}
                onToggle={toggleLastUpdatedDatePicker}
                label="Last Updated Date Range"
                isLoading={filterLoadingStates.lastUpdatedDate}
                className="last-updated-date-picker"
              />
            </div>
                <div className="floating-input-group select-status">
            <div
              className={`status-dropdown ${
                isStatusDropdownOpen ? "open" : ""
              } ${modernFilters.status.length > 0 ? "has-selection" : ""}`}
              onKeyDown={handleDropdownKeyDown}
            >
              <div className="dropdown-header" onClick={toggleStatusDropdown}>
                <div className="dropdown-header-content">
                  <span className="dropdown-text">
                    {modernFilters.status.length > 0
                      ? modernFilters.status.join(", ")
                      : "Select Status"}
                  </span>
                  {modernFilters.status.length > 0 && (
                    <span className="status-badge">
                      {modernFilters.status.length}
                    </span>
                  )}
                </div>
                <svg
                  className={`dropdown-arrow ${
                    isStatusDropdownOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <div
                className={`dropdown-content ${
                  isStatusDropdownOpen ? "open" : ""
                }`}
              >
                {/* Search Status Section */}
                <div className="status-search-wrapper">
                  <input
                    type="text"
                    className="status-search-input"
                    placeholder="Search statuses..."
                    value={statusSearchTerm}
                    onChange={(e) => setStatusSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {statusSearchTerm ? (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => setStatusSearchTerm("")}
                      title="Clear search"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  ) : (
                    <svg
                      className="search-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  )}
                </div>

                {/* Add Status Input Section */}
                <div className="custom-status-input-wrapper">
                  <input
                    type="text"
                    className="custom-status-input"
                    placeholder="Add status..."
                    value={customStatusInput}
                    onChange={(e) => setCustomStatusInput(e.target.value)}
                    onKeyPress={handleCustomStatusKeyPress}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    className="add-status-btn"
                    onClick={handleAddCustomStatus}
                    disabled={!customStatusInput.trim()}
                    title="Add status"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>

                {/* Selected Statuses with Remove Option */}
                {modernFilters.status.length > 0 && (
                  <div className="selected-statuses-wrapper">
                    <div className="selected-statuses-header">
                      <span className="selected-statuses-label">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Selected ({modernFilters.status.length})
                      </span>
                      <button
                        type="button"
                        className="clear-all-btn"
                        onClick={() => onStatusChange("SELECT_ALL", false, [])}
                        title="Clear all selections"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="selected-statuses-tags">
                      {modernFilters.status.map((status) => (
                        <span key={status} className="status-tag">
                          {status}
                          <button
                            type="button"
                            className="remove-status-btn"
                            onClick={() => handleRemoveStatus(status)}
                            title={`Remove ${status}`}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="status-divider"></div>

                {/* Predefined Statuses with Checkboxes */}
                <div className="checkboxes-grid">
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={
                        modernFilters.status.length === availableStatuses.length
                      }
                      onChange={(e) => {
                        const allStatuses = availableStatuses;
                        if (e.target.checked) {
                          // Select all - set all statuses for UI display but backend will get empty array
                          onStatusChange("SELECT_ALL", true, allStatuses);
                        } else {
                          // Deselect all - clear all statuses
                          onStatusChange("SELECT_ALL", false, []);
                        }
                      }}
                    />
                    <span className="checkbox-label-text">Select All</span>
                  </label>
                  {filteredStatuses().length > 0 ? (
                    filteredStatuses().map((status) => (
                      <label key={status} className="status-checkbox-label">
                        <input
                          type="checkbox"
                          checked={modernFilters.status.includes(status)}
                          onChange={(e) =>
                            onStatusChange(status, e.target.checked)
                          }
                        />
                        <span className="checkbox-label-text">{status}</span>
                      </label>
                    ))
                  ) : (
                    <div className="no-results">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                      <p>No statuses found</p>
                      <small>Try a different search term</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>

      
        </div>
      </div>
    </section>
  );
};

export default React.memo(MerchantFilters);
